import React, {useContext, useEffect, useState} from 'react';
import {Button, Input, Modal, Form, Select, Space, Tooltip, Typography, Table, Popconfirm, Card, Empty} from "antd";
import Tag from "../Tag/Tag";
import {connect, useDispatch} from 'react-redux';
import {SketchPicker} from "react-color";
import {setTags, addEntity, updateEntityLabels, removeEntity, changeConfig} from "../util/redux";
import {AuthContext} from "../Auth/AuthProvider";
import {getRandomColor} from "../util/shared_functions";
import {getEntities, saveConfig} from "../util/redux/asyncActions";
import {PlusOutlined, DeleteOutlined, QuestionCircleOutlined, SaveOutlined} from "@ant-design/icons";

const {Text} = Typography;

const select = state => {
    const tags = state.request.tags;
    const entities = state.request.entities;
    const config = state.request.config;
    const unsavedConfig = state.request.unsavedConfig;
    return {tags, entities, config, unsavedConfig};
};

function TagManager(props) {

    const {tags, entities, config, unsavedConfig} = props;
    const dispatch = useDispatch();
    const userManager = useContext(AuthContext);

    const updateConfig = (newEntities) => {
        try {
            const parsed = config ? JSON.parse(config) : {};
            parsed.entities = newEntities;
            dispatch(changeConfig(JSON.stringify(parsed, null, 2)));
        } catch (e) {
            // config not yet loaded or invalid — skip
        }
    };

    const [color, setColor] = useState(getRandomColor());
    const [isAddTagOpen, setIsAddTagOpen] = useState(false);
    const [tagName, setTagName] = useState("");
    const [entityLabels, setEntityLabels] = useState([]);

    useEffect(() => {
        dispatch(getEntities({userManager}));
    }, []);

    const handleColorChange = ({hex}) => {
        setColor(hex);
    };

    const handleAddTag = () => {
        setColor(getRandomColor());
        setTagName("");
        setEntityLabels([]);
        setIsAddTagOpen(true);
    };

    const handleOk = () => {
        if (tagName !== "") {
            const upperTag = tagName.toUpperCase();
            dispatch(setTags([...tags, {tag: upperTag, color}]));
            dispatch(addEntity({entityType: upperTag, labels: entityLabels}));
            const newEntities = {...entities, [upperTag]: {labels: entityLabels || [], color}};
            updateConfig(newEntities);
            setIsAddTagOpen(false);
            setEntityLabels([]);
        }
    };

    const handleCancel = () => {
        setIsAddTagOpen(false);
        setEntityLabels([]);
    };

    const handleRemoveTag = (index) => {
        const tag = tags[index];
        const newTags = tags.filter((_, i) => i !== index);
        dispatch(setTags(newTags));
        dispatch(removeEntity(tag.tag));
        const newEntities = {...entities};
        delete newEntities[tag.tag];
        updateConfig(newEntities);
    };

    const handleLabelsChange = (tagName, newLabels) => {
        dispatch(updateEntityLabels({entityType: tagName, labels: newLabels}));
        dispatch(addEntity({entityType: tagName, labels: newLabels}));
        const existingColor = tags.find(t => t.tag === tagName)?.color;
        const newEntities = {...entities, [tagName]: {labels: newLabels, ...(existingColor && {color: existingColor})}};
        updateConfig(newEntities);
    };

    const tableData = tags.map((tag, i) => ({
        key: i,
        index: i,
        tag: tag.tag,
        color: tag.color,
        labels: entities?.[tag.tag]?.labels || [],
    }));

    const columns = [
        {
            title: "Tag",
            key: "tag",
            width: 200,
            render: (_, record) => (
                <Tag
                    tagName={record.tag}
                    color={record.color}
                    index={record.index}
                    closable={false}
                />
            )
        },
        {
            title: () => (
                <Space>
                    Labels
                    <Tooltip title="Labels help Dynamic Tagging detect this entity type. Example: for PERSON add 'person', 'name', 'first_name'. Tags without labels are only used for manual tagging and Trained Tagging.">
                        <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                    </Tooltip>
                </Space>
            ),
            key: "labels",
            render: (_, record) => (
                <Select
                    mode="tags"
                    style={{width: "100%"}}
                    placeholder="Add labels for Dynamic Tagging..."
                    value={record.labels}
                    onChange={(newLabels) => handleLabelsChange(record.tag, newLabels)}
                />
            )
        },
        {
            title: "",
            key: "actions",
            width: 50,
            render: (_, record) => (
                <Popconfirm
                    title={`Remove "${record.tag}"?`}
                    description="This will remove the tag and its labels."
                    onConfirm={() => handleRemoveTag(record.index)}
                >
                    <Button type="text" danger icon={<DeleteOutlined/>} size="small"/>
                </Popconfirm>
            )
        }
    ];

    return (
        <div style={{maxWidth: 900, margin: "0 auto"}}>
            <Card
                title={
                    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                        <Space>
                            <Text strong style={{fontSize: 15}}>Entity Tags</Text>
                            <Text type="secondary" style={{fontWeight: 400}}>({tags.length})</Text>
                        </Space>
                        <Space>
                            <Button
                                icon={<SaveOutlined/>}
                                onClick={() => dispatch(saveConfig({userManager, config}))}
                                disabled={!unsavedConfig}
                                type={unsavedConfig ? "primary" : "default"}
                            >
                                Save
                            </Button>
                            <Button
                                icon={<PlusOutlined/>}
                                onClick={handleAddTag}
                            >
                                Add Tag
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Text type="secondary" style={{fontSize: 13, display: "block", marginBottom: 16}}>
                    Tags define the entity types used across the application.
                    In <strong>Prepare Data</strong>, use them to annotate training data.
                    In <strong>Configure</strong>, they map to detection rules.
                    Add <strong>labels</strong> to enable Dynamic Tagging — the system will detect entities matching those labels automatically.
                </Text>
                {tags.length === 0 ? (
                    <Empty
                        description="No tags defined yet. Add your first entity tag to get started."
                        style={{padding: "40px 0"}}
                    >
                        <Button type="primary" icon={<PlusOutlined/>} onClick={handleAddTag}>
                            Add Tag
                        </Button>
                    </Empty>
                ) : (
                    <Table
                        dataSource={tableData}
                        columns={columns}
                        pagination={false}
                        size="middle"
                    />
                )}
            </Card>

            <Modal
                title="Add a new Tag"
                open={isAddTagOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                okButtonProps={{disabled: !tagName}}
            >
                <Form layout="vertical" style={{marginTop: 16}}>
                    <Form.Item label="Name" required>
                        <Input
                            placeholder="e.g. PERSON, EMAIL, PHONE"
                            value={tagName}
                            onChange={(e) => setTagName(e.target.value)}
                        />
                    </Form.Item>

                    <Form.Item label="Color">
                        <SketchPicker
                            color={color}
                            onChangeComplete={handleColorChange}
                            onChange={handleColorChange}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <Space>
                                Labels
                                <Tooltip title="Optional. Labels for Dynamic Tagging to detect this entity type. Example: for PERSON add 'person', 'name', 'first_name'.">
                                    <QuestionCircleOutlined/>
                                </Tooltip>
                            </Space>
                        }
                    >
                        <Select
                            mode="tags"
                            placeholder="Type labels and press Enter..."
                            value={entityLabels}
                            onChange={setEntityLabels}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default connect(select)(TagManager);
